import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'chess-uno:sound-enabled'
const CHANGE_EVENT = 'chess-uno:sound-change'
let audio: AudioContext | null = null
let inMemoryEnabled = true

export function soundEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return inMemoryEnabled
  }
}

function subscribe(listener: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, listener)
  window.addEventListener('storage', listener)
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener)
    window.removeEventListener('storage', listener)
  }
}

export function useSoundEnabled(): boolean {
  return useSyncExternalStore(subscribe, soundEnabled, () => true)
}

function context(): AudioContext | null {
  try {
    audio ??= new AudioContext()
    if (audio.state === 'suspended') void audio.resume().catch(() => {})
    return audio
  } catch {
    return null
  }
}

/** Unlock Web Audio during the player's first gesture, before a remote event arrives. */
export function unlockSound(): void {
  if (soundEnabled()) context()
}

export function setSoundEnabled(enabled: boolean): void {
  inMemoryEnabled = enabled
  try {
    window.localStorage.setItem(STORAGE_KEY, String(enabled))
  } catch {
    // Keep the setting for this page when storage is unavailable.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT))
  if (enabled) unlockSound()
  else if (audio) void audio.suspend().catch(() => {})
}

export type SoundEvent = 'move' | 'join' | 'turn'

export function playSound(event: SoundEvent): void {
  if (!soundEnabled()) return
  const ctx = context()
  if (!ctx) return
  const notes = event === 'move' ? [420] : event === 'join' ? [523, 659] : [660, 880]
  const start = ctx.currentTime
  notes.forEach((frequency, index) => {
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    const at = start + index * 0.11
    oscillator.type = event === 'move' ? 'triangle' : 'sine'
    oscillator.frequency.setValueAtTime(frequency, at)
    gain.gain.setValueAtTime(0.0001, at)
    gain.gain.exponentialRampToValueAtTime(event === 'move' ? 0.13 : 0.09, at + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + (event === 'move' ? 0.12 : 0.18))
    oscillator.connect(gain).connect(ctx.destination)
    oscillator.start(at)
    oscillator.stop(at + (event === 'move' ? 0.13 : 0.19))
  })
}
